import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { api } from '@/lib/api';
import { showError } from '@/lib/toast';
import { ArrowLeft, Mail } from 'lucide-react';

export function ForgotPassword() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/auth/forgot-password', { email });
      // Backend always returns a generic success to prevent enumeration, so we
      // unconditionally send the user to the reset page. If the account does
      // not actually exist they'll fail at the OTP step with a clear error.
      navigate('/reset-password', { state: { email: email.toLowerCase() } });
    } catch (error: any) {
      showError(error.response?.data?.message || t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-muted/30 px-4">
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center mb-8">
          <Link to="/" className="flex flex-col items-center gap-3">
            <img src="/logo.png" alt="SetOpenClaw" className="h-20 w-20 rounded-2xl object-contain" />
            <span className="text-2xl font-bold">SetOpenClaw</span>
          </Link>
        </div>
        <Card>
          <CardContent className="p-8">
            <div className="text-center mb-6">
              <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <Mail className="h-8 w-8 text-primary" />
              </div>
              <h1 className="text-2xl font-bold mb-2">
                {t('auth.forgotPassword.title')}
              </h1>
              <p className="text-sm text-muted-foreground">
                {t('auth.forgotPassword.subtitle')}
              </p>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>{t('auth.login.email')}</Label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="name@example.com"
                  required
                />
              </div>
              <Button type="submit" className="w-full" disabled={loading || !email}>
                {loading ? t('auth.forgotPassword.sending') : t('auth.forgotPassword.sendResetLink')}
              </Button>
            </form>
            <div className="mt-4 text-center">
              <Link to="/login" className="text-sm text-primary hover:underline inline-flex items-center gap-1">
                <ArrowLeft className="h-3 w-3" />
                {t('auth.forgotPassword.backToLogin')}
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
